import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Bell, CheckSquare, Trash2, Cake } from 'lucide-react';
import { toast } from 'sonner';
import ShareWhatsappButton from './ShareWhatsappButton';
import { safeTime } from '../lib/formatters';
import { calendarTaskLabelParts } from '../utils/calendarTaskLabel';
import { birthdayKeysForRange } from '../utils/birthdayCalendar';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const WEEKDAYS_ES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const toLocalKey = (dateStr) => {
  if (!dateStr) return null;
  // If it's a plain YYYY-MM-DD, keep the same day regardless of timezone
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${day}`;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export default function CalendarView() {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(today);
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      // La ventana de datos que necesita CalendarView es la GRILLA VISIBLE
      // (6x7), que suele incluir días del mes anterior y del siguiente.
      // Traemos recordatorios EXPANDIDOS para ese rango — el backend se
      // encarga de generar 1 ítem por cada ocurrencia (weekly/daily/monthly/
      // yearly) del mismo template, sin crear registros nuevos en Mongo.
      const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const shift = (first.getDay() + 6) % 7;
      const start = new Date(first);
      start.setDate(first.getDate() - shift);
      const end = new Date(start);
      end.setDate(start.getDate() + 41);
      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const fromParam = iso(start);
      const toParam = iso(end);

      const [tasksRes, remindersRes, birthdaysRes] = await Promise.all([
        axios.get(`${API}/api/tasks`, { withCredentials: true }),
        axios.get(`${API}/api/reminders/expanded?from=${fromParam}&to=${toParam}`, { withCredentials: true }),
        axios.get(`${API}/api/family/upcoming-birthdays?days=366`, { withCredentials: true }),
      ]);
      setTasks(tasksRes.data);
      // El endpoint devuelve { reminders: [...] } (con ocurrencias expandidas
      // y `series_id` para poder mapear al template original).
      setReminders(remindersRes.data?.reminders || []);
      setBirthdays(birthdaysRes.data?.members || []);
    } catch (error) {
      toast.error('Error al cargar el calendario');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => {
    loadData();
    const handler = () => loadData();
    window.addEventListener('vida:data-changed', handler);
    return () => window.removeEventListener('vida:data-changed', handler);
  }, [loadData]);

  const deleteTask = async (taskId) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    try {
      await axios.delete(`${API}/api/tasks/${taskId}`, { withCredentials: true });
      toast.success('Tarea eliminada');
      window.dispatchEvent(new CustomEvent('vida:data-changed'));
    } catch {
      toast.error('No pudimos eliminar la tarea');
    }
  };

  const deleteReminder = async (reminderId) => {
    if (!window.confirm('¿Eliminar este recordatorio?')) return;
    try {
      await axios.delete(`${API}/api/reminders/${reminderId}`, { withCredentials: true });
      toast.success('Recordatorio eliminado');
      window.dispatchEvent(new CustomEvent('vida:data-changed'));
    } catch {
      toast.error('No pudimos eliminar el recordatorio');
    }
  };

  const deleteBirthday = async (birthdayId) => {
    if (!window.confirm('¿Eliminar este cumpleaños?')) return;
    try {
      await axios.delete(`${API}/api/family/birthdays/${birthdayId}`, { withCredentials: true });
      toast.success('Cumpleaños eliminado');
      window.dispatchEvent(new CustomEvent('vida:data-changed'));
    } catch {
      toast.error('No pudimos eliminar el cumpleaños');
    }
  };

  // Group tasks & reminders by local YYYY-MM-DD
  const eventsByDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      const k = toLocalKey(t.due_date);
      if (!k) return;
      if (!map[k]) map[k] = { tasks: [], reminders: [], birthdays: [] };
      map[k].tasks.push(t);
    });
    reminders.forEach((r) => {
      const k = toLocalKey(r.remind_at);
      if (!k) return;
      if (!map[k]) map[k] = { tasks: [], reminders: [], birthdays: [] };
      map[k].reminders.push(r);
    });
    // Cumpleaños: evento ANUAL RECURRENTE. Pintamos en TODOS los años dentro
    // del rango del selector (year-5 .. year+5) usando el MM-DD del birthday.
    // NO usamos `days_until` para no perder el aniversario del año actual
    // cuando ya pasó (bug K1.7 corregido).
    const currentYear = new Date().getFullYear();
    birthdays.forEach((b) => {
      const keys = birthdayKeysForRange(b.birthday, currentYear);
      for (const k of keys) {
        if (!map[k]) map[k] = { tasks: [], reminders: [], birthdays: [] };
        map[k].birthdays.push(b);
      }
    });
    return map;
  }, [tasks, reminders, birthdays]);

  // Build 6x7 grid of the visible month, starting on Monday
  const gridDays = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    // getDay: 0=Sun..6=Sat. Convert to Mon-start (Mon=0..Sun=6)
    const shift = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(first.getDate() - shift);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [cursor]);

  const goPrevMonth = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNextMonth = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goToday = () => {
    const now = new Date();
    setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(now);
  };

  const onChangeMonth = (e) =>
    setCursor(new Date(cursor.getFullYear(), Number(e.target.value), 1));
  const onChangeYear = (e) =>
    setCursor(new Date(Number(e.target.value), cursor.getMonth(), 1));

  const years = useMemo(() => {
    const y = today.getFullYear();
    const list = [];
    for (let i = y - 5; i <= y + 5; i++) list.push(i);
    return list;
  }, [today]);

  const selectedKey = toLocalKey(selectedDate);
  const dayEvents = eventsByDay[selectedKey] || { tasks: [], reminders: [], birthdays: [] };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-12 h-12 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-view">
      <div>
        <h1
          className="text-4xl sm:text-5xl font-extrabold text-foreground mb-2"
          
        >
          Calendario
        </h1>
        <p className="text-muted-foreground">Tus eventos y fechas importantes</p>
      </div>

      <div className="bg-surface rounded-2xl shadow-md p-3 sm:p-6">
        {/* Header controls: prev / month+year / next / today */}
        <div className="flex items-center gap-1 sm:gap-3 mb-4">
          <button
            data-testid="cal-prev-month"
            onClick={goPrevMonth}
            className="p-1.5 sm:p-2 rounded-full hover:bg-muted text-foreground flex-shrink-0"
            aria-label="Mes anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
            <select
              data-testid="cal-month-select"
              value={cursor.getMonth()}
              onChange={onChangeMonth}
              className="flex-1 min-w-0 px-2 py-2 bg-surface-2 rounded-lg text-sm sm:text-base font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#D97706]"
            >
              {MONTHS_ES.map((m, i) => (
                <option key={m} value={i}>{m}</option>
              ))}
            </select>

            <select
              data-testid="cal-year-select"
              value={cursor.getFullYear()}
              onChange={onChangeYear}
              className="px-2 py-2 bg-surface-2 rounded-lg text-sm sm:text-base font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-[#D97706]"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <button
            data-testid="cal-next-month"
            onClick={goNextMonth}
            className="p-1.5 sm:p-2 rounded-full hover:bg-muted text-foreground flex-shrink-0"
            aria-label="Mes siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <button
            data-testid="cal-today-btn"
            onClick={goToday}
            className="px-3 py-2 text-xs sm:text-sm font-semibold bg-[#1E3A5F] text-white rounded-full hover:bg-[#152B47] transition-all flex-shrink-0"
          >
            Hoy
          </button>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-0.5 sm:gap-2 mb-1 sm:mb-2">
          {WEEKDAYS_ES.map((wd) => (
            <div
              key={wd}
              className="text-center text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider py-1 sm:py-2"
            >
              {wd}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-0.5 sm:gap-2">
          {gridDays.map((d) => {
            const key = toLocalKey(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = isSameDay(d, today);
            const isSelected = isSameDay(d, selectedDate);
            const ev = eventsByDay[key];
            const taskCount = ev?.tasks.length || 0;
            const reminderCount = ev?.reminders.length || 0;
            const birthdayCount = ev?.birthdays?.length || 0;
            const hasEvents = taskCount + reminderCount + birthdayCount > 0;
            return (
              <button
                key={key}
                data-testid={`cal-day-${key}`}
                onClick={() => setSelectedDate(d)}
                className={`aspect-square flex flex-col items-center justify-start p-0.5 sm:p-2 rounded-md sm:rounded-lg transition-all text-xs sm:text-sm border ${
                  isSelected
                    ? 'bg-[#1E3A5F] text-white border-foreground shadow-md'
                    : isToday
                    ? 'bg-amber-100 dark:bg-amber-500/15 text-foreground border-[#D97706]/40'
                    : inMonth
                    ? 'bg-surface text-foreground border-slate-100 hover:bg-surface-2'
                    : 'bg-surface-2 text-muted-foreground border-transparent hover:bg-muted'
                }`}
              >
                <span className={`font-semibold ${isSelected ? 'text-white' : ''}`}>
                  {d.getDate()}
                </span>
                {hasEvents && (
                  <div className="flex gap-0.5 mt-auto pb-0.5">
                    {taskCount > 0 && (
                      <span
                        className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${
                          isSelected ? 'bg-surface' : 'bg-[#4CAF50]'
                        }`}
                      />
                    )}
                    {reminderCount > 0 && (
                      <span
                        className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${
                          isSelected ? 'bg-surface' : 'bg-[#D97706]'
                        }`}
                      />
                    )}
                    {birthdayCount > 0 && (
                      <span
                        data-testid={`cal-day-${key}-bday-dot`}
                        className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${
                          isSelected ? 'bg-surface' : 'bg-[#7C3AED]'
                        }`}
                      />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      <div className="bg-surface rounded-2xl shadow-md p-5 sm:p-8" data-testid="cal-day-detail">
        <h2 className="text-xl font-bold text-foreground mb-4">
          {selectedDate.toLocaleDateString('es-AR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </h2>

        {dayEvents.tasks.length === 0 && dayEvents.reminders.length === 0 && (dayEvents.birthdays?.length || 0) === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-muted-foreground">Sin eventos para este día</p>
          </div>
        ) : (
          <div className="space-y-4">
            {(dayEvents.birthdays?.length || 0) > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Cake className="w-4 h-4 text-[#7C3AED]" /> Cumpleaños
                </h3>
                <div className="space-y-2">
                  {dayEvents.birthdays.map((b) => (
                    <div
                      key={b.id}
                      data-testid={`calendar-birthday-${b.id}`}
                      className="flex items-center gap-3 p-3 bg-violet-100 dark:bg-violet-500/10 rounded-lg"
                    >
                      <span className="text-xl" aria-hidden="true">🎂</span>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{b.name}</p>
                        {typeof b.age_turning === 'number' && (
                          <p className="text-xs text-muted-foreground">Cumple {b.age_turning} años</p>
                        )}
                      </div>
                      <button
                        data-testid={`delete-calendar-birthday-${b.id}`}
                        onClick={() => deleteBirthday(b.id)}
                        title="Eliminar cumpleaños"
                        className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dayEvents.tasks.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Tareas
                </h3>
                <div className="space-y-2">
                  {dayEvents.tasks.map((task) => {
                    const { title: tTitle, timeSuffix } = calendarTaskLabelParts(task);
                    return (
                    <div
                      key={task.id}
                      data-testid={`calendar-task-${task.id}`}
                      className="flex items-center gap-3 p-3 bg-surface-2 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">
                          <span>{tTitle}</span>
                          {timeSuffix && (
                            <span
                              className="ml-2 text-sm font-normal text-muted-foreground"
                              data-testid={`calendar-task-time-${task.id}`}
                            >
                              {timeSuffix}
                            </span>
                          )}
                        </p>
                        {task.description && (
                          <p className="text-sm text-muted-foreground">{task.description}</p>
                        )}
                      </div>
                      <ShareWhatsappButton
                        title={task.title}
                        date={task.due_date}
                        description={task.description}
                        kind="task"
                        testId={`share-calendar-task-${task.id}`}
                      />
                      <button
                        data-testid={`delete-calendar-task-${task.id}`}
                        onClick={() => deleteTask(task.id)}
                        title="Eliminar tarea"
                        className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {dayEvents.reminders.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Bell className="w-4 h-4 text-primary" /> Recordatorios
                </h3>
                <div className="space-y-2">
                  {dayEvents.reminders.map((reminder) => (
                    <div
                      key={reminder.id}
                      data-testid={`calendar-reminder-${reminder.id}`}
                      className="flex items-start gap-3 p-3 bg-amber-100 dark:bg-amber-500/10 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{reminder.title}</p>
                        {reminder.description && (
                          <p className="text-sm text-muted-foreground">{reminder.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {safeTime(reminder.remind_at, { hour: '2-digit', minute: '2-digit' }, '')}
                        </p>
                      </div>
                      <ShareWhatsappButton
                        title={reminder.title}
                        date={reminder.remind_at}
                        description={reminder.description}
                        kind={reminder.type === 'payment' ? 'bill' : 'reminder'}
                        testId={`share-calendar-reminder-${reminder.id}`}
                      />
                      <button
                        data-testid={`delete-calendar-reminder-${reminder.id}`}
                        onClick={() => deleteReminder(reminder.id)}
                        title="Eliminar recordatorio"
                        className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
